import { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { X, Lightbulb } from 'lucide-react';

// Storage key for tracking shown hints
const HINTS_STORAGE_KEY = 'storynode_shown_hints';

// All available contextual hints
const HINTS = {
  // Mind Map Page hints
  node_hover: {
    id: 'node_hover',
    message: "Click on any node to see options like 'Explain with AI', add notes, or hear it read aloud!",
    position: 'bottom',
    delay: 1500,
  },
  mindmap_navigation: {
    id: 'mindmap_navigation',
    message: "Pan by dragging the background. Zoom with scroll or the controls on the left.",
    position: 'bottom-left',
    delay: 2000,
  },
  audio_player: {
    id: 'audio_player',
    message: "Press Space to play/pause, M to mute, and arrow keys to seek.",
    position: 'top',
    delay: 1000,
  },
  share_button: {
    id: 'share_button',
    message: "Share your mind map with others! They can view, chat, and even collaborate.",
    position: 'bottom',
    delay: 500,
  },
  study_mode: {
    id: 'study_mode',
    message: "Test your knowledge with AI-generated quizzes based on your mind map!",
    position: 'bottom',
    delay: 500,
  },
  layout_selector: {
    id: 'layout_selector',
    message: "Try different layouts: Tree, Radial, or Hierarchical view.",
    position: 'bottom',
    delay: 3000,
  },
  
  // Dashboard hints
  first_book: {
    id: 'first_book',
    message: "Click 'View Mind Map' to explore your book's structure visually!",
    position: 'top',
    delay: 1500,
  },
  search_books: {
    id: 'search_books',
    message: "Search for any book title - our AI will create a comprehensive mind map!",
    position: 'bottom',
    delay: 2000,
  },
  templates: {
    id: 'templates',
    message: "Start faster with pre-built templates for different book types.",
    position: 'bottom',
    delay: 2500,
  },
  
  // Shared Mind Map hints
  collab_editing: {
    id: 'collab_editing',
    message: "Hover over nodes to see edit options. Changes sync in real-time!",
    position: 'bottom',
    delay: 2000,
  },
  chat_feature: {
    id: 'chat_feature',
    message: "Use @username to mention others. React with emojis and reply to messages!",
    position: 'left',
    delay: 1500,
  },
  cursor_presence: {
    id: 'cursor_presence',
    message: "See other users' cursors in real-time as they explore the mind map.",
    position: 'top',
    delay: 3000,
  },
};

// Context for managing hints globally
const HintContext = createContext(null);

export const useHints = () => {
  const context = useContext(HintContext);
  if (!context) {
    throw new Error('useHints must be used within a HintProvider');
  }
  return context;
};

// Provider component
export function HintProvider({ children }) {
  const [shownHints, setShownHints] = useState(() => {
    try {
      const stored = localStorage.getItem(HINTS_STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });
  
  const [activeHint, setActiveHint] = useState(null);
  const [hintPosition, setHintPosition] = useState({ x: 0, y: 0 });

  // Save shown hints to localStorage
  useEffect(() => {
    localStorage.setItem(HINTS_STORAGE_KEY, JSON.stringify(shownHints));
  }, [shownHints]);

  const showHint = useCallback((hintId, targetElement = null) => {
    const hint = HINTS[hintId];
    if (!hint || shownHints.includes(hintId)) return;

    // Calculate position based on target element
    if (targetElement) {
      const rect = targetElement.getBoundingClientRect();
      let x = rect.left + rect.width / 2;
      let y = rect.bottom + 10;

      switch (hint.position) {
        case 'top':
          y = rect.top - 10;
          break;
        case 'bottom':
          y = rect.bottom + 10;
          break;
        case 'left':
          x = rect.left - 10;
          y = rect.top + rect.height / 2;
          break;
        case 'right':
          x = rect.right + 10;
          y = rect.top + rect.height / 2;
          break;
        case 'bottom-left':
          x = rect.left;
          y = rect.bottom + 10;
          break;
        default:
          break;
      }

      setHintPosition({ x, y, position: hint.position });
    } else {
      // Center on screen if no target
      setHintPosition({ 
        x: window.innerWidth / 2, 
        y: window.innerHeight / 2,
        position: 'center'
      });
    }

    setTimeout(() => {
      setActiveHint(hint);
    }, hint.delay || 0);
  }, [shownHints]);

  const dismissHint = useCallback((hintId) => {
    setActiveHint(null);
    if (hintId && !shownHints.includes(hintId)) {
      setShownHints(prev => [...prev, hintId]);
    }
  }, [shownHints]);

  const resetHints = useCallback(() => {
    setShownHints([]);
    localStorage.removeItem(HINTS_STORAGE_KEY);
  }, []);

  const hasSeenHint = useCallback((hintId) => {
    return shownHints.includes(hintId);
  }, [shownHints]);

  return (
    <HintContext.Provider value={{ showHint, dismissHint, resetHints, hasSeenHint, activeHint }}>
      {children}
      {activeHint && (
        <ContextualTooltip
          hint={activeHint}
          position={hintPosition}
          onDismiss={() => dismissHint(activeHint.id)}
        />
      )}
    </HintContext.Provider>
  );
}

// Tooltip component
function ContextualTooltip({ hint, position, onDismiss }) {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Animate in
    requestAnimationFrame(() => setIsVisible(true));
    
    // Auto-dismiss after 8 seconds
    const timer = setTimeout(() => {
      setIsVisible(false);
      setTimeout(onDismiss, 300);
    }, 8000);

    return () => clearTimeout(timer);
  }, [onDismiss]);

  const getPositionStyles = () => {
    const { x, y, position: pos } = position;
    
    switch (pos) {
      case 'top':
        return {
          left: `${x}px`,
          bottom: `${window.innerHeight - y}px`,
          transform: 'translateX(-50%)',
        };
      case 'left':
        return {
          right: `${window.innerWidth - x}px`,
          top: `${y}px`,
          transform: 'translateY(-50%)',
        };
      case 'right':
        return {
          left: `${x}px`,
          top: `${y}px`,
          transform: 'translateY(-50%)',
        };
      case 'bottom-left':
        return {
          left: `${x}px`,
          top: `${y}px`,
        };
      case 'center':
        return {
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
        };
      case 'bottom':
      default:
        return {
          left: `${x}px`,
          top: `${y}px`,
          transform: 'translateX(-50%)',
        };
    }
  };

  const getArrowStyles = () => {
    const { position: pos } = position;
    
    switch (pos) {
      case 'top':
        return 'bottom-0 left-1/2 -translate-x-1/2 translate-y-full border-l-transparent border-r-transparent border-b-transparent border-t-primary/90';
      case 'left':
        return 'right-0 top-1/2 translate-x-full -translate-y-1/2 border-t-transparent border-b-transparent border-r-transparent border-l-primary/90';
      case 'right':
        return 'left-0 top-1/2 -translate-x-full -translate-y-1/2 border-t-transparent border-b-transparent border-l-transparent border-r-primary/90';
      case 'bottom':
      case 'bottom-left':
      default:
        return 'top-0 left-1/2 -translate-x-1/2 -translate-y-full border-l-transparent border-r-transparent border-t-transparent border-b-primary/90';
    }
  };

  return (
    <div
      className={`fixed z-[200] max-w-xs transition-all duration-300 ${
        isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
      }`}
      style={getPositionStyles()}
    >
      <div className="relative bg-primary/95 text-primary-foreground rounded-xl shadow-xl p-4 backdrop-blur-sm">
        {/* Arrow */}
        {position.position !== 'center' && (
          <div className={`absolute w-0 h-0 border-8 ${getArrowStyles()}`} />
        )}
        
        {/* Content */}
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 p-1.5 bg-primary-foreground/20 rounded-lg">
            <Lightbulb className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm leading-relaxed">{hint.message}</p>
          </div>
          <button
            onClick={onDismiss}
            className="flex-shrink-0 p-1 hover:bg-primary-foreground/20 rounded-full transition-colors"
            aria-label="Dismiss hint"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        
        {/* Got it button */}
        <button
          onClick={onDismiss}
          className="mt-3 w-full text-xs font-medium py-1.5 px-3 bg-primary-foreground/20 hover:bg-primary-foreground/30 rounded-lg transition-colors"
        >
          Got it!
        </button>
      </div>
    </div>
  );
}

// Hook for triggering hints on element interaction
export function useHintTrigger(hintId, options = {}) {
  const { showHint, hasSeenHint } = useHints();
  const { triggerOn = 'hover', delay = 0 } = options;
  
  const triggerHint = useCallback((event) => {
    if (hasSeenHint(hintId)) return;
    
    const target = event.currentTarget || event.target;
    setTimeout(() => {
      showHint(hintId, target);
    }, delay);
  }, [hintId, showHint, hasSeenHint, delay]);

  const getProps = useCallback(() => {
    if (hasSeenHint(hintId)) return {};
    
    switch (triggerOn) {
      case 'hover':
        return { onMouseEnter: triggerHint };
      case 'click':
        return { onClick: triggerHint };
      case 'focus':
        return { onFocus: triggerHint };
      default:
        return {};
    }
  }, [triggerOn, triggerHint, hasSeenHint, hintId]);

  return { triggerHint, getProps, hasSeenHint: hasSeenHint(hintId) };
}

// Component wrapper for easy hint triggering
export function HintTrigger({ hintId, children, triggerOn = 'hover', delay = 0, className = '' }) {
  const { triggerHint, hasSeenHint } = useHintTrigger(hintId, { triggerOn, delay });

  if (hasSeenHint) {
    return <div className={className}>{children}</div>;
  }

  const props = {
    hover: { onMouseEnter: triggerHint },
    click: { onClick: triggerHint },
    focus: { onFocus: triggerHint },
  }[triggerOn] || {};

  return (
    <div className={className} {...props}>
      {children}
    </div>
  );
}

// Auto-show hint after component mounts
export function useAutoHint(hintId, targetRef, delay = 2000) {
  const { showHint, hasSeenHint } = useHints();

  useEffect(() => {
    if (hasSeenHint(hintId)) return;

    const timer = setTimeout(() => {
      showHint(hintId, targetRef?.current);
    }, delay);

    return () => clearTimeout(timer);
  }, [hintId, showHint, hasSeenHint, targetRef, delay]);
}

export default ContextualTooltip;
